/**
 * NativeVideoPlayer — resume-from-position video component for ScenarioOS.
 *
 * Handles three URL types automatically:
 *   • YouTube  — IFrame Player API (loaded once via <script>)
 *   • Vimeo    — Vimeo Player SDK  (loaded once via <script>)
 *   • Direct   — HTML5 <video> element (MP4, WebM, etc.)
 *
 * Features:
 *   - Fetches saved position from backend on mount and seeks to it
 *   - Auto-saves position to backend every 10 seconds
 *   - Progress bar (read-only, shows % watched)
 *   - "Mark as Complete" button
 *   - Playback speed selector (HTML5 only)
 *   - Fires onComplete callback when video ends or learner marks complete
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { emitLearnerEvent } from '../services/learnerEvents';
import PauseAndReflectOverlay from './overlays/PauseAndReflectOverlay';
import ClickableZoneOverlay from './overlays/ClickableZoneOverlay';
import ConfidenceRatingOverlay from './overlays/ConfidenceRatingOverlay';
import PredictionOverlay from './overlays/PredictionOverlay';
import BranchingOverlay from './overlays/BranchingOverlay';
import HintRevealOverlay from './overlays/HintRevealOverlay';
import ChapterSidebar from './chapters/ChapterSidebar';
import InteractiveTranscript from './chapters/InteractiveTranscript';

const API_BASE = process.env.REACT_APP_SCENARIO_OS_API || '/scenario-os';
const SAVE_INTERVAL_MS = 10_000;

// ─── URL classification ────────────────────────────────────────────────────

function classifyUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );
  if (yt) return { kind: 'youtube', videoId: yt[1] };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d{5,})/);
  if (vm) return { kind: 'vimeo', videoId: vm[1] };
  return { kind: 'file', videoId: null };
}

// ─── Backend helpers ───────────────────────────────────────────────────────

function getToken() {
  return localStorage.getItem('scenario_os_token');
}

async function fetchSavedPosition(segmentId) {
  try {
    const token = getToken();
    if (!token || !segmentId) return 0;
    const res = await fetch(
      `${API_BASE}/curriculum/video-progress/${encodeURIComponent(segmentId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return 0;
    const data = await res.json();
    return data.position_seconds || 0;
  } catch {
    return 0;
  }
}

async function savePosition(segmentId, positionSeconds, durationSeconds, completed) {
  try {
    const token = getToken();
    if (!token || !segmentId) return;
    await fetch(`${API_BASE}/curriculum/video-progress`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        segment_id: segmentId,
        position_seconds: positionSeconds,
        duration_seconds: durationSeconds,
        completed,
      }),
    });
  } catch {
    // non-critical — silent failure
  }
}

// ─── YouTube IFrame API loader (singleton promise) ─────────────────────────

let _ytApiPromise = null;

function loadYouTubeAPI() {
  if (_ytApiPromise) return _ytApiPromise;
  if (window.YT && window.YT.Player) {
    _ytApiPromise = Promise.resolve();
    return _ytApiPromise;
  }
  _ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve();
    };
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });
  return _ytApiPromise;
}

// ─── Vimeo Player SDK loader (singleton promise) ───────────────────────────

let _vmApiPromise = null;

function loadVimeoAPI() {
  if (_vmApiPromise) return _vmApiPromise;
  if (window.Vimeo && window.Vimeo.Player) {
    _vmApiPromise = Promise.resolve();
    return _vmApiPromise;
  }
  _vmApiPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://player.vimeo.com/api/player.js';
    s.onload = resolve;
    s.onerror = () => {
      _vmApiPromise = null; // allow retry on next mount
      reject(new Error('Failed to load Vimeo SDK'));
    };
    document.head.appendChild(s);
  });
  return _vmApiPromise;
}

// ─── YouTube sub-player ────────────────────────────────────────────────────

function YouTubePlayer({ videoId, savedPosition, onTimeUpdate, onComplete, onPlayerReady }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    loadYouTubeAPI().then(() => {
      if (!mountedRef.current || !containerRef.current) return;

      // YouTube requires a real DOM element to mount into
      const mountDiv = document.createElement('div');
      containerRef.current.appendChild(mountDiv);

      const player = new window.YT.Player(mountDiv, {
        videoId,
        playerVars: {
          start: Math.floor(savedPosition || 0),
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: (e) => {
            if (!mountedRef.current) return;
            playerRef.current = e.target;

            // Expose the YT player instance to NativeVideoPlayer so it can
            // call pauseVideo() / playVideo() for MCQ checkpoint overlays.
            if (onPlayerReady) onPlayerReady(e.target);

            // Poll position every second to update parent progress bar
            intervalRef.current = setInterval(() => {
              if (!mountedRef.current) return;
              const p = playerRef.current;
              if (p && typeof p.getCurrentTime === 'function') {
                try {
                  onTimeUpdate(p.getCurrentTime() || 0, p.getDuration() || 0);
                } catch {}
              }
            }, 1000);
          },
          onStateChange: (e) => {
            if (!mountedRef.current) return;
            if (e.data === window.YT.PlayerState.ENDED) {
              onComplete();
            }
          },
        },
      });
      playerRef.current = player;
    }).catch(() => {
      // YouTube IFrame API blocked/failed — still show an embed so learners
      // (and Eva V-03) see a real youtube.com iframe rather than a blank mount.
      if (!mountedRef.current || !containerRef.current) return;
      if (containerRef.current.querySelector('iframe')) return;
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?start=${Math.floor(savedPosition || 0)}&playsinline=1&rel=0`;
      iframe.title = 'YouTube video player';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.setAttribute('frameborder', '0');
      iframe.style.cssText = 'width:100%;height:100%;border:0;position:absolute;inset:0';
      containerRef.current.appendChild(iframe);
    });

    return () => {
      mountedRef.current = false;
      clearInterval(intervalRef.current);
      try { playerRef.current?.destroy(); } catch {}
    };
    // videoId and savedPosition are stable for the lifetime of this mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, savedPosition]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', inset: 0 }}
    />
  );
}

// ─── Vimeo sub-player ─────────────────────────────────────────────────────

function VimeoPlayer({ videoId, savedPosition, onTimeUpdate, onComplete }) {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    loadVimeoAPI().then(() => {
      if (!mountedRef.current || !iframeRef.current) return;

      const player = new window.Vimeo.Player(iframeRef.current);
      playerRef.current = player;

      player.ready().then(() => {
        if (!mountedRef.current) return;
        if (savedPosition > 0) {
          player.setCurrentTime(savedPosition).catch(() => {});
        }
      }).catch(() => {});

      player.on('timeupdate', (data) => {
        if (!mountedRef.current) return;
        onTimeUpdate(data.seconds || 0, data.duration || 0);
      });

      player.on('ended', () => {
        if (!mountedRef.current) return;
        onComplete();
      });
    }).catch(() => {
      // Vimeo SDK failed — iframe still renders, just no position tracking
    });

    return () => {
      mountedRef.current = false;
      try {
        playerRef.current?.off('timeupdate');
        playerRef.current?.off('ended');
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, savedPosition]);

  return (
    <iframe
      ref={iframeRef}
      src={`https://player.vimeo.com/video/${videoId}`}
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      title="Vimeo video player"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
    />
  );
}

// ─── HTML5 file sub-player ────────────────────────────────────────────────

function FilePlayer({ src, savedPosition, speed, onTimeUpdate, onComplete, captionsEnabled, sceneId, videoElementRef, onPause, onSeekBackward }) {
  const videoRef = useRef(null);
  const restoredRef = useRef(false);
  // track-4: capture position just before a seek so we can detect backwards seeks
  const seekFromMsRef = useRef(0);

  useEffect(() => {
    if (videoElementRef) videoElementRef.current = videoRef.current;
  });

  // Apply speed changes immediately
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed]);

  // Reset restoration flag when src changes
  useEffect(() => { restoredRef.current = false; }, [src]);

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!restoredRef.current && savedPosition > 0) {
      v.currentTime = savedPosition;
      restoredRef.current = true;
    }
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    onTimeUpdate(v.currentTime || 0, v.duration || 0);
    // Keep seekFromMs current so a subsequent onSeeking captures the right position
    seekFromMsRef.current = (v.currentTime || 0) * 1000;
  };

  // track-4: capture "from" position before seek begins
  const handleSeeking = () => {
    // seekFromMs was already updated by the most recent timeupdate; nothing more needed.
  };

  // track-4: emit video_replayed for backwards seeks
  const handleSeeked = () => {
    const v = videoRef.current;
    if (!v) return;
    const toMs = (v.currentTime || 0) * 1000;
    const fromMs = seekFromMsRef.current;
    if (toMs < fromMs - 1000) {
      // Only emit when seeking back by more than 1 second
      if (onSeekBackward) onSeekBackward(fromMs, toMs);
    }
    // Update seekFrom after seek settles
    seekFromMsRef.current = toMs;
  };

  const vttUrl = sceneId ? `${API_BASE}/coach/transcripts/${sceneId}/vtt` : null;

  return (
    <video
      ref={videoRef}
      src={src}
      controls
      crossOrigin="anonymous"
      onLoadedMetadata={handleLoadedMetadata}
      onTimeUpdate={handleTimeUpdate}
      onEnded={onComplete}
      onPause={onPause}
      onSeeking={handleSeeking}
      onSeeked={handleSeeked}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: '#000' }}
    >
      {vttUrl && captionsEnabled && (
        <track
          kind="subtitles"
          src={vttUrl}
          srcLang="en"
          label="English"
          default
        />
      )}
    </video>
  );
}

// ─── Main NativeVideoPlayer ────────────────────────────────────────────────

export default function NativeVideoPlayer({ src, segmentId, onComplete, title, mcqMarkers, sceneId, onTimeUpdateExternal, seekRef, scene, chapterMarkers, confidenceRatingEnabled, learnerId }) {
  const info = useMemo(() => classifyUrl(src), [src]);
  const [savedPosition, setSavedPosition] = useState(null);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [answeredMarkers, setAnsweredMarkers] = useState(new Set());
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [chapterSidebarOpen, setChapterSidebarOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  // Post-MCQ confidence rating: store the skillId of the last answered MCQ
  const [postMCQSkillId, setPostMCQSkillId] = useState(null);
  const videoElementRef = useRef(null);

  const resolvedChapterMarkers = chapterMarkers || scene?.chapter_markers || [];

  useEffect(() => {
    if (seekRef) {
      seekRef.current = (seconds) => {
        if (videoElementRef.current) {
          videoElementRef.current.currentTime = seconds;
        }
      };
    }
  }, [seekRef]);

  const durationRef = useRef(0);
  const lastSaveAtRef = useRef(0);
  const currentTimeRef = useRef(0);
  const pausedForQuizRef = useRef(false);
  // Holds the YT.Player instance for YouTube videos — populated via onPlayerReady callback.
  // Used to call pauseVideo() / playVideo() when MCQ checkpoints fire/dismiss.
  const ytPlayerRef = useRef(null);

  const sortedMarkers = useMemo(() => {
    if (!mcqMarkers?.length) return [];
    return [...mcqMarkers]
      .filter((m) => {
        const type = m.overlay_type || 'mcq';
        if (type === 'mcq') return m.question?.prompt && m.question?.choices?.length;
        // Non-MCQ overlays only need a question string
        return m.question;
      })
      .sort((a, b) => (a.timestamp_ms || 0) - (b.timestamp_ms || 0));
  }, [mcqMarkers]);

  useEffect(() => {
    setSavedPosition(null);
    setProgress(0);
    setCompleted(false);
    setActiveQuiz(null);
    setAnsweredMarkers(new Set());
    fetchSavedPosition(segmentId).then(setSavedPosition);
  }, [segmentId]);

  const handleTimeUpdate = useCallback((currentTime, duration) => {
    currentTimeRef.current = currentTime;
    setCurrentTimeMs(currentTime * 1000);
    if (duration > 0) {
      durationRef.current = duration;
      setProgress(Math.min(100, Math.round((currentTime / duration) * 100)));
    }
    if (onTimeUpdateExternal) onTimeUpdateExternal(currentTime);
    const now = Date.now();
    if (now - lastSaveAtRef.current >= SAVE_INTERVAL_MS) {
      lastSaveAtRef.current = now;
      savePosition(segmentId, currentTime, duration || durationRef.current, false);
    }

    // Check for MCQ checkpoint — fire once per marker
    if (!pausedForQuizRef.current && sortedMarkers.length > 0) {
      const currentMs = currentTime * 1000;
      for (const marker of sortedMarkers) {
        const markerMs = marker.timestamp_ms || 0;
        if (
          !answeredMarkers.has(marker.marker_id) &&
          currentMs >= markerMs &&
          currentMs < markerMs + 2000
        ) {
          pausedForQuizRef.current = true;
          setActiveQuiz(marker);
          // Pause the video when quiz triggers — HTML5 and YouTube paths
          if (videoElementRef.current) {
            if (videoElementRef.current.pause) {
              videoElementRef.current.pause();       // HTML5 <video>
            }
          }
          // YouTube IFrame API path (ytPlayerRef set via onPlayerReady callback)
          try { ytPlayerRef.current?.pauseVideo?.(); } catch {}
          break;
        }
      }
    }
  }, [segmentId, sortedMarkers, answeredMarkers, onTimeUpdateExternal]);

  const handleQuizDismiss = useCallback((wasAnswered = false) => {
    if (activeQuiz) {
      setAnsweredMarkers((prev) => new Set([...prev, activeQuiz.marker_id]));
      // Show post-MCQ confidence rating for MCQ types when enabled
      const markerType = activeQuiz.overlay_type || 'mcq';
      if (wasAnswered && confidenceRatingEnabled && markerType === 'mcq') {
        const resolvedLearnerId = learnerId
          || localStorage.getItem('scenario_os_learner_id')
          || '';
        // Use skill_id from marker or fall back to sceneId
        const skillForRating = activeQuiz.skill_id || sceneId || activeQuiz.marker_id;
        if (resolvedLearnerId && skillForRating) {
          setPostMCQSkillId(skillForRating);
          // Delay video resume until confidence rating is dismissed
          return;
        }
      }
    }
    setActiveQuiz(null);
    pausedForQuizRef.current = false;
    // Resume video playback after quiz is dismissed — HTML5 and YouTube paths
    if (videoElementRef.current) {
      if (videoElementRef.current.play) {
        videoElementRef.current.play().catch(() => {}); // HTML5 <video>
      }
    }
    // YouTube IFrame API path
    try { ytPlayerRef.current?.playVideo?.(); } catch {}
  }, [activeQuiz, confidenceRatingEnabled, learnerId, sceneId]);

  const handlePostMCQRatingDone = useCallback(() => {
    setPostMCQSkillId(null);
    setActiveQuiz(null);
    pausedForQuizRef.current = false;
    if (videoElementRef.current) {
      if (videoElementRef.current.play) {
        videoElementRef.current.play().catch(() => {});
      }
    }
    try { ytPlayerRef.current?.playVideo?.(); } catch {}
  }, []);

  const handleComplete = useCallback(() => {
    setCompleted((prev) => {
      if (prev) return true;
      savePosition(segmentId, durationRef.current, durationRef.current, true);
      if (onComplete) onComplete();
      return true;
    });
    setProgress(100);
  }, [segmentId, onComplete]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      const v = videoElementRef.current;
      if (v) {
        if (v.paused) v.play();
        else v.pause();
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const v = videoElementRef.current;
      if (v) v.currentTime = Math.min(v.duration, v.currentTime + 5);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const v = videoElementRef.current;
      if (v) v.currentTime = Math.max(0, v.currentTime - 5);
    }
  }, []);

  if (!src) return null;

  if (savedPosition === null) {
    return (
      <div style={wrapStyle}>
        <div style={{ color: '#9ca3af', fontSize: 13, padding: '32px 0', textAlign: 'center' }}>
          Loading video…
        </div>
      </div>
    );
  }

  // Lazy-load VideoQuizOverlay only when needed (MCQ type)
  const VideoQuizOverlay = (activeQuiz && (!activeQuiz.overlay_type || activeQuiz.overlay_type === 'mcq'))
    ? require('./VideoQuizOverlay').default
    : null;

  return (
    <div style={wrapStyle} role="region" aria-label={title || 'Video player'}>
      {title && (
        <div style={titleBarStyle}>{title}</div>
      )}

      {/* 16:9 video container */}
      <div
        style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label="Video player"
      >
        {/* Chapter sidebar overlay */}
        <ChapterSidebar
          chapters={resolvedChapterMarkers}
          currentTimeMs={currentTimeMs}
          onSeek={(timestampMs) => {
            const seconds = timestampMs / 1000;
            if (videoElementRef.current) {
              if (videoElementRef.current.currentTime !== undefined) {
                videoElementRef.current.currentTime = seconds;
              } else if (videoElementRef.current.seekTo) {
                videoElementRef.current.seekTo(seconds, true);
              }
            }
          }}
          isOpen={chapterSidebarOpen}
          onToggle={() => setChapterSidebarOpen((v) => !v)}
        />
        {info?.kind === 'youtube' && (
          <YouTubePlayer
            videoId={info.videoId}
            savedPosition={savedPosition}
            onTimeUpdate={handleTimeUpdate}
            onComplete={handleComplete}
            onPlayerReady={(player) => { ytPlayerRef.current = player; }}
          />
        )}
        {info?.kind === 'vimeo' && (
          <VimeoPlayer
            videoId={info.videoId}
            savedPosition={savedPosition}
            onTimeUpdate={handleTimeUpdate}
            onComplete={handleComplete}
          />
        )}
        {info?.kind === 'file' && (
          <FilePlayer
            src={src}
            savedPosition={savedPosition}
            speed={speed}
            onTimeUpdate={handleTimeUpdate}
            onComplete={handleComplete}
            captionsEnabled={captionsEnabled}
            sceneId={sceneId}
            videoElementRef={videoElementRef}
            onPause={() => emitLearnerEvent({
              event_type: 'video_paused',
              timestamp_ms: currentTimeRef.current * 1000,
            })}
            onSeekBackward={(fromMs, toMs) => emitLearnerEvent({
              event_type: 'video_replayed',
              from_ms: fromMs,
              to_ms: toMs,
            })}
          />
        )}
        {!info && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>
            Unable to determine video type.
          </div>
        )}

        {/* Post-MCQ Anki-style confidence rating */}
        {postMCQSkillId && (
          <ConfidenceRatingOverlay
            learnerId={learnerId || localStorage.getItem('scenario_os_learner_id') || ''}
            skillId={postMCQSkillId}
            confidenceRatingEnabled
            onRated={() => {}}
            onDismissed={handlePostMCQRatingDone}
          />
        )}

        {/* Quiz / overlay checkpoint */}
        {activeQuiz && (() => {
          const type = activeQuiz.overlay_type || 'mcq';
          if (type === 'mcq' && VideoQuizOverlay) {
            return (
              <VideoQuizOverlay
                question={activeQuiz.question}
                onAnswer={(correct) => {
                  if (correct) handleQuizDismiss(true);
                }}
                onDismiss={() => handleQuizDismiss(false)}
                maxAttempts={activeQuiz.max_attempts || 2}
                continueLabel="Continue Video"
                markerId={activeQuiz.marker_id}
              />
            );
          }
          if (type === 'pause_and_reflect') {
            return (
              <PauseAndReflectOverlay
                question={activeQuiz.question}
                markerId={activeQuiz.marker_id}
                sceneId={sceneId}
                domain={activeQuiz.domain}
                onDismiss={handleQuizDismiss}
              />
            );
          }
          if (type === 'clickable_zone') {
            return (
              <ClickableZoneOverlay
                question={activeQuiz.question}
                correctZone={activeQuiz.correct_zone}
                markerId={activeQuiz.marker_id}
                sceneId={sceneId}
                domain={activeQuiz.domain}
                gridSize={activeQuiz.grid_size}
                onDismiss={handleQuizDismiss}
              />
            );
          }
          if (type === 'confidence_rating') {
            return (
              <ConfidenceRatingOverlay
                question={activeQuiz.question}
                markerId={activeQuiz.marker_id}
                sceneId={sceneId}
                domain={activeQuiz.domain}
                onDismiss={handleQuizDismiss}
              />
            );
          }
          if (type === 'prediction') {
            return (
              <PredictionOverlay
                marker={activeQuiz}
                sceneId={sceneId}
                onClose={handleQuizDismiss}
              />
            );
          }
          if (type === 'branching') {
            return (
              <BranchingOverlay
                marker={activeQuiz}
                sceneId={sceneId}
                onBranch={() => setActiveQuiz(null)}
                onClose={handleQuizDismiss}
              />
            );
          }
          if (type === 'hint_reveal') {
            return (
              <HintRevealOverlay
                marker={activeQuiz}
                sceneId={sceneId}
                onClose={handleQuizDismiss}
              />
            );
          }
          return null;
        })()}
      </div>

      {/* Interactive transcript panel */}
      {transcriptOpen && (
        <div style={transcriptPanelStyle}>
          <InteractiveTranscript
            segments={scene?.transcript_segments || scene?.domain_metadata?.transcript_segments || []}
            currentTimeMs={currentTimeMs}
            onSeek={(timestamp_ms) => {
              const seconds = timestamp_ms / 1000;
              if (videoElementRef.current) {
                if (videoElementRef.current.currentTime !== undefined) {
                  videoElementRef.current.currentTime = seconds;
                } else if (videoElementRef.current.seekTo) {
                  videoElementRef.current.seekTo(seconds, true);
                }
              }
            }}
          />
        </div>
      )}

      {/* Controls bar */}
      <div style={controlsBarStyle}>
        {/* Progress bar with checkpoint markers */}
        <div style={progressTrackStyle}>
          <div style={{ ...progressFillStyle, width: `${progress}%` }} />
          {sortedMarkers.map((m) => {
            const pct = durationRef.current > 0
              ? Math.min(100, ((m.timestamp_ms / 1000) / durationRef.current) * 100)
              : 0;
            return (
              <div
                key={m.marker_id}
                title={`Checkpoint at ${Math.round(m.timestamp_ms / 1000)}s`}
                style={{
                  position: 'absolute',
                  left: `${pct}%`,
                  top: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: answeredMarkers.has(m.marker_id) ? '#22c55e' : '#f59e0b',
                  border: '1px solid #0f172a',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                }}
              />
            );
          })}
        </div>

        <div style={controlsRowStyle}>
          <span style={{ color: '#9ca3af', fontSize: 12 }}>
            {progress}% watched
            {sortedMarkers.length > 0 && (
              <span style={{ marginLeft: 8, color: '#64748b' }}>
                {answeredMarkers.size}/{sortedMarkers.length} checkpoints
              </span>
            )}
          </span>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {sceneId && info?.kind === 'file' && (
              <button
                onClick={() => setCaptionsEnabled((v) => !v)}
                aria-label="Toggle captions"
                aria-pressed={captionsEnabled}
                title={captionsEnabled ? 'Disable captions' : 'Enable captions'}
                style={{
                  ...speedSelectStyle,
                  fontWeight: 700,
                  fontSize: 11,
                  padding: '3px 8px',
                  cursor: 'pointer',
                  background: captionsEnabled ? '#3b82f6' : '#1f2937',
                  color: captionsEnabled ? '#fff' : '#d1d5db',
                  border: captionsEnabled ? '1px solid #3b82f6' : '1px solid #374151',
                  borderRadius: 4,
                }}
              >
                CC
              </button>
            )}
            <button
              onClick={() => setTranscriptOpen((v) => !v)}
              aria-label="Toggle transcript"
              aria-pressed={transcriptOpen}
              title={transcriptOpen ? 'Hide transcript' : 'Show transcript'}
              style={{
                ...speedSelectStyle,
                fontWeight: 600,
                fontSize: 11,
                padding: '3px 8px',
                cursor: 'pointer',
                background: transcriptOpen ? '#6366f1' : '#1f2937',
                color: transcriptOpen ? '#fff' : '#d1d5db',
                border: transcriptOpen ? '1px solid #6366f1' : '1px solid #374151',
                borderRadius: 4,
              }}
            >
              Transcript
            </button>
            {info?.kind === 'file' && (
              <select
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
                aria-label="Playback speed"
                style={speedSelectStyle}
              >
                {[0.75, 1, 1.25, 1.5, 2].map((s) => (
                  <option key={s} value={s}>{s}x</option>
                ))}
              </select>
            )}

            <button
              onClick={handleComplete}
              style={completed ? completedBtnStyle : markBtnStyle}
            >
              {completed ? '✓ Completed' : 'Mark as Complete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const wrapStyle = {
  background: '#0a0a0f',
  borderRadius: 10,
  overflow: 'hidden',
  marginBottom: 16,
  border: '1px solid #1f2937',
};

const titleBarStyle = {
  color: '#d1d5db',
  fontSize: 13,
  fontWeight: 600,
  padding: '8px 14px',
  background: '#111827',
  borderBottom: '1px solid #1f2937',
};

const controlsBarStyle = {
  background: '#111827',
  padding: '10px 14px',
};

const progressTrackStyle = {
  position: 'relative',
  background: '#374151',
  borderRadius: 999,
  height: 4,
  overflow: 'visible',
  marginBottom: 10,
};

const progressFillStyle = {
  background: '#3b82f6',
  height: '100%',
  transition: 'width 1s linear',
  borderRadius: 999,
};

const controlsRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  flexWrap: 'wrap',
};

const speedSelectStyle = {
  background: '#1f2937',
  color: '#d1d5db',
  border: '1px solid #374151',
  borderRadius: 4,
  padding: '3px 8px',
  fontSize: 12,
  cursor: 'pointer',
};

const markBtnStyle = {
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '5px 14px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};

const completedBtnStyle = {
  ...markBtnStyle,
  background: '#059669',
  cursor: 'default',
};

const transcriptPanelStyle = {
  background: '#0f172a',
  borderTop: '1px solid #1f2937',
  borderBottom: '1px solid #1f2937',
};
